<template>
  <AdminLayout
    :page-title="isEdit ? 'Edit Article' : 'New Article'"
    fill
  >
    <template #header-actions>
      <div class="hidden sm:flex items-center gap-3">
        <button type="button" @click="handleCancel" class="dashboard-secondary-btn">
          Cancel
        </button>
        <button
          type="button"
          :disabled="saving || loadingArticle"
          class="dashboard-primary-btn"
          @click="handleSave"
        >
          {{ saving ? "Saving..." : isEdit ? "Save Changes" : "Create Article" }}
        </button>
      </div>
    </template>

    <div class="article-form flex flex-1 flex-col min-h-0 w-full max-w-[1600px] mx-auto">
      <div
        v-if="loadingArticle"
        class="flex flex-1 items-center justify-center text-on-surface-variant"
      >
        Loading article...
      </div>

      <form
        v-else
        :key="createFormKey"
        autocomplete="off"
        @submit.prevent="handleSave"
        class="article-form-card bg-surface rounded-xl border border-outline-variant/30"
      >
        <div
          class="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(280px,340px)] xl:grid-cols-[minmax(0,1fr)_minmax(300px,380px)]"
        >
          <!-- Main content column -->
          <section
            class="flex flex-col gap-6 p-5 sm:p-6 lg:p-8 border-b lg:border-b-0 lg:border-r border-outline-variant/20"
          >
            <div>
              <label class="field-label" for="article-title">Title *</label>
              <input
                id="article-title"
                v-model="formData.title"
                type="text"
                required
                :maxlength="ARTICLE_LIMITS.title"
                placeholder="Article title"
                class="field-input text-base sm:text-lg"
              />
            </div>

            <div class="content-editor-block">
              <label id="content-label" class="field-label">
                Content *
              </label>
              <div
                ref="editorContainer"
                class="wysiwyg-editor-container"
                :class="{ 'editor-image-uploading': uploadingEditorImage }"
                role="textbox"
                aria-label="Article content editor"
                aria-labelledby="content-label"
                aria-multiline="true"
              ></div>
              <input
                ref="editorImageInput"
                type="file"
                accept="image/*"
                class="hidden"
                @change="handleEditorImageSelect"
              />
            </div>

            <div>
              <label class="field-label" for="article-excerpt">Excerpt *</label>
              <textarea
                id="article-excerpt"
                v-model="formData.excerpt"
                required
                rows="3"
                placeholder="Short summary shown in article listings"
                class="field-input resize-y min-h-[88px]"
              ></textarea>
            </div>
          </section>

          <!-- Metadata & image sidebar -->
          <aside
            class="flex flex-col gap-5 p-5 sm:p-6 lg:p-6 xl:p-8 bg-surface-container-low/40 lg:overflow-y-auto"
          >
            <h2 class="text-sm font-semibold uppercase tracking-wide text-on-surface-variant">
              Details
            </h2>

            <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-4">
              <div>
                <fieldset>
                  <legend id="category-label" class="field-label mb-2">
                    Categories *
                  </legend>
                  <div class="flex flex-col gap-2">
                    <label
                      v-for="cat in ARTICLE_CATEGORIES"
                      :key="cat"
                      class="flex cursor-pointer items-center gap-2 text-sm text-on-surface"
                    >
                      <input
                        v-model="formData.categories"
                        type="checkbox"
                        :value="cat"
                        class="h-4 w-4 rounded border-outline-variant text-primary focus:ring-primary/30"
                        aria-labelledby="category-label"
                      />
                      {{ cat }}
                    </label>
                  </div>
                </fieldset>
              </div>

              <div>
                <label class="field-label" for="article-author">Author *</label>
                <input
                  id="article-author"
                  v-model="formData.author"
                  type="text"
                  required
                  :maxlength="ARTICLE_LIMITS.author"
                  class="field-input"
                />
              </div>

              <div class="sm:col-span-2 lg:col-span-1">
                <label class="field-label" for="article-date">Date *</label>
                <input
                  id="article-date"
                  v-model="formData.date"
                  type="date"
                  required
                  class="field-input"
                />
              </div>
            </div>

            <div class="flex items-center justify-between rounded-lg border border-outline-variant/30 p-4">
              <div>
                <p class="text-sm font-medium text-on-surface">Publish Status</p>
                <p class="text-xs text-on-surface-variant mt-1">
                  {{ formData.is_published ? "Visible on the public site" : "Saved as draft, not public" }}
                </p>
              </div>
              <label class="relative inline-flex items-center cursor-pointer">
                <input
                  v-model="formData.is_published"
                  type="checkbox"
                  class="sr-only peer"
                />
                <div
                  class="w-11 h-6 bg-outline-variant/40 rounded-full peer peer-checked:bg-primary peer-focus:ring-2 peer-focus:ring-primary/30 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full"
                ></div>
                <span class="ml-3 text-sm font-medium text-on-surface">
                  {{ formData.is_published ? "Published" : "Draft" }}
                </span>
              </label>
            </div>

            <div class="flex flex-col gap-3">
              <label class="field-label" for="article-image">Featured Image *</label>

              <div
                v-if="formData.image"
                class="image-preview-wrap rounded-lg border border-outline-variant/30 overflow-hidden bg-surface"
              >
                <img
                  :src="formData.image"
                  alt="Article image preview"
                  class="w-full aspect-video object-cover"
                />
              </div>

              <input
                id="article-image"
                v-model="formData.image"
                type="text"
                required
                placeholder="https://... (upload below or paste URL)"
                class="field-input"
                @blur="formData.image = formData.image.trim()"
              />

              <div
                class="upload-zone rounded-lg border-2 border-dashed transition-colors"
                :class="
                  isDragging
                    ? 'border-primary bg-primary/5'
                    : 'border-outline-variant/40 bg-surface'
                "
                @dragover.prevent="isDragging = true"
                @dragleave.prevent="isDragging = false"
                @drop.prevent="handleDrop"
              >
                <input
                  ref="fileInput"
                  type="file"
                  accept="image/*"
                  class="hidden"
                  @change="handleFileSelect"
                />
                <div class="p-5 text-center">
                  <svg
                    class="w-8 h-8 mx-auto mb-2 text-on-surface-variant/60"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="1.5"
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                  <p class="text-sm text-on-surface-variant mb-3">
                    Drag & drop or choose a file
                  </p>
                  <button
                    type="button"
                    @click="fileInput?.click()"
                    :disabled="uploading"
                    class="dashboard-secondary-btn w-full sm:w-auto"
                  >
                    {{ uploading ? "Uploading..." : "Upload image" }}
                  </button>
                  <p v-if="uploading" class="text-xs text-primary mt-2">
                    Uploading to ImageKit...
                  </p>
                </div>
              </div>
            </div>
          </aside>
        </div>

        <!-- Mobile / tablet action bar -->
        <div
          class="sm:hidden sticky bottom-0 flex items-center justify-end gap-3 px-5 py-4 border-t border-outline-variant/30 bg-surface/95 backdrop-blur-sm"
        >
          <button type="button" @click="handleCancel" class="dashboard-secondary-btn flex-1">
            Cancel
          </button>
          <button
            type="submit"
            :disabled="saving"
            class="dashboard-primary-btn flex-1"
          >
            {{ saving ? "Saving..." : isEdit ? "Save" : "Create" }}
          </button>
        </div>
      </form>
    </div>
  </AdminLayout>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from "vue";
import { useRoute, useRouter, onBeforeRouteUpdate } from "vue-router";
import AdminLayout from "../../components/admin/AdminLayout.vue";
import {
  getArticleByIdAdmin,
  saveArticle as saveArticleToDb,
  formatArticleSaveError,
} from "../../utils/articles.js";
import {
  ARTICLE_LIMITS,
  isValidImageUrl,
} from "../../utils/articleSlug.js";
import { ARTICLE_CATEGORIES } from "../../utils/articleCategories.js";
import { uploadImage } from "../../utils/imagekit.js";
import { showAdminToast } from "../../utils/adminToast.js";

const route = useRoute();
const router = useRouter();

const isEdit = computed(() => !!route.params.id);
const loadingArticle = ref(!!route.params.id);
const saving = ref(false);
const uploading = ref(false);
const isDragging = ref(false);
const editorContainer = ref(null);
const fileInput = ref(null);
const editorImageInput = ref(null);
const uploadingEditorImage = ref(false);
let quillEditor = null;
let editorPasteHandler = null;

const getDefaultFormData = () => ({
  id: "",
  title: "",
  excerpt: "",
  categories: ["Business"],
  author: "",
  date: new Date().toISOString().split("T")[0],
  image: "",
  content: "",
  is_published: false,
});

const createFormKey = ref(
  typeof crypto?.randomUUID === "function"
    ? crypto.randomUUID()
    : String(Date.now()),
);

const resetCreateForm = () => {
  formData.value = getDefaultFormData();
  createFormKey.value =
    typeof crypto?.randomUUID === "function"
      ? crypto.randomUUID()
      : String(Date.now());
  if (quillEditor) {
    quillEditor.root.innerHTML = "";
  }
};

const formData = ref(getDefaultFormData());

const parseDateForInput = (dateString) => {
  if (!dateString) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) return dateString;
  try {
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      return date.toISOString().split("T")[0];
    }
  } catch (e) {
    console.error("Error parsing date:", e);
  }
  return "";
};

const estimateReadTime = (htmlContent) => {
  if (!htmlContent) return "";
  const text = htmlContent
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  const words = text ? text.split(" ").length : 0;
  const minutes = Math.max(1, Math.round(words / 200));
  return `${minutes} min read`;
};


const insertImageIntoEditor = (url) => {
  if (!quillEditor || !url) return;
  const range = quillEditor.getSelection(true);
  quillEditor.insertEmbed(range.index, "image", url);
  quillEditor.setSelection(range.index + 1);
};

const uploadEditorImageFile = async (file) => {
  if (!file || !file.type.startsWith("image/")) {
    showAdminToast("Please select a valid image file", "error");
    return;
  }

  uploadingEditorImage.value = true;
  try {
    const url = await uploadImage(file);
    insertImageIntoEditor(url);
  } catch (error) {
    console.error("Error uploading image:", error);
    showAdminToast("Failed to upload image. Try again.", "error");
  } finally {
    uploadingEditorImage.value = false;
  }
};

const imageHandler = () => {
  if (uploadingEditorImage.value) return;
  editorImageInput.value?.click();
};

const handleEditorImageSelect = (event) => {
  const file = event.target.files?.[0];
  if (file) uploadEditorImageFile(file);
  event.target.value = "";
};

const initEditor = async () => {
  if (!editorContainer.value) return;

  destroyEditor();

  if (typeof window.Quill === "undefined") {
    if (!document.getElementById("quill-css")) {
      const link = document.createElement("link");
      link.id = "quill-css";
      link.rel = "stylesheet";
      link.href = "https://cdn.quilljs.com/1.3.6/quill.snow.css";
      document.head.appendChild(link);
    }

    if (!document.getElementById("quill-js")) {
      await new Promise((resolve) => {
        const script = document.createElement("script");
        script.id = "quill-js";
        script.src = "https://cdn.quilljs.com/1.3.6/quill.js";
        script.onload = resolve;
        document.head.appendChild(script);
      });
    }
  }

  await nextTick();
  if (editorContainer.value && window.Quill) {
    quillEditor = new window.Quill(editorContainer.value, {
      theme: "snow",
      modules: {
        toolbar: {
          container: [
            [{ header: [1, 2, 3, false] }],
            ["bold", "italic", "underline", "strike"],
            [{ list: "ordered" }, { list: "bullet" }],
            [{ color: [] }, { background: [] }],
            ["link", "image", "blockquote", "code-block"],
            ["clean"],
          ],
          handlers: {
            image: imageHandler,
          },
        },
      },
      placeholder: "Start writing your article content...",
    });

    if (formData.value.content) {
      quillEditor.root.innerHTML = formData.value.content;
    }

    quillEditor.on("text-change", () => {
      const root = quillEditor.root;
      const hasContent =
        quillEditor.getText().trim() !== "" ||
        root.querySelector("img, video, iframe");
      root.classList.toggle("ql-blank", !hasContent);
      formData.value.content = root.innerHTML;
    });

    editorPasteHandler = async (event) => {
      const items = event.clipboardData?.items;
      if (!items || uploadingEditorImage.value) return;

      for (const item of items) {
        if (item.type.startsWith("image/")) {
          event.preventDefault();
          const file = item.getAsFile();
          if (file) await uploadEditorImageFile(file);
          return;
        }
      }
    };

    quillEditor.root.addEventListener("paste", editorPasteHandler);
  }
};

const destroyEditor = () => {
  if (quillEditor) {
    if (editorPasteHandler) {
      quillEditor.root.removeEventListener("paste", editorPasteHandler);
      editorPasteHandler = null;
    }
    quillEditor = null;
  }
  if (editorContainer.value) {
    editorContainer.value.innerHTML = "";
  }
};

const loadArticle = async () => {
  if (!isEdit.value) return;

  loadingArticle.value = true;
  try {
    const article = await getArticleByIdAdmin(route.params.id);
    if (!article) {
      showAdminToast("Article not found", "error");
      router.push("/admin/dashboard");
      return;
    }

    formData.value = {
      id: article.id,
      title: article.title,
      excerpt: article.excerpt,
      categories: [...(article.categories?.length ? article.categories : [])],
      author: article.author,
      date: parseDateForInput(article.date),
      image: article.image,
      content: article.content,
      is_published: article.is_published === true,
    };
  } catch (error) {
    console.error("Error loading article:", error);
    showAdminToast("Error loading article", "error");
  } finally {
    loadingArticle.value = false;
  }
};

const uploadFile = async (file) => {
  if (!file || !file.type.startsWith("image/")) {
    showAdminToast("Please select a valid image file", "error");
    return;
  }

  uploading.value = true;
  try {
    const url = await uploadImage(file);
    formData.value.image = url;
    showAdminToast("Image uploaded successfully", "success");
  } catch (error) {
    console.error("Error uploading image:", error);
    showAdminToast("Failed to upload image. Try again or use a URL.", "error");
  } finally {
    uploading.value = false;
    isDragging.value = false;
  }
};

const handleDrop = (event) => {
  isDragging.value = false;
  const file = event.dataTransfer?.files?.[0];
  if (file) uploadFile(file);
};

const handleFileSelect = (event) => {
  const file = event.target.files?.[0];
  if (file) uploadFile(file);
  event.target.value = "";
};

const handleSave = async () => {
  if (quillEditor) {
    formData.value.content = quillEditor.root.innerHTML;
  }

  const trimmedImage = formData.value.image?.trim() || "";

  if (
    !formData.value.title?.trim() ||
    !formData.value.excerpt?.trim() ||
    !formData.value.categories?.length ||
    !formData.value.author?.trim() ||
    !formData.value.date ||
    !trimmedImage ||
    !formData.value.content
  ) {
    showAdminToast("Please fill in all required fields", "error");
    return;
  }

  if (!isValidImageUrl(trimmedImage)) {
    showAdminToast(
      "Featured image must be a valid URL. Upload an image or paste a full https:// link.",
      "error"
    );
    return;
  }

  saving.value = true;
  try {
    await saveArticleToDb({
      ...formData.value,
      title: formData.value.title.trim(),
      excerpt: formData.value.excerpt.trim(),
      author: formData.value.author.trim(),
      image: trimmedImage,
      readTime: estimateReadTime(formData.value.content),
    });

    const isPublished = formData.value.is_published === true;

    sessionStorage.setItem(
      "adminFlash",
      JSON.stringify({
        message: isPublished
          ? isEdit.value
            ? "Article updated successfully!"
            : "Article published successfully!"
          : "Article saved as draft.",
        type: "success",
      })
    );
    router.push("/admin/dashboard");
  } catch (error) {
    console.error("Error saving article:", error);
    showAdminToast(formatArticleSaveError(error), "error");
  } finally {
    saving.value = false;
  }
};

const handleCancel = () => {
  router.push("/admin/dashboard");
};

const setupCreateForm = async () => {
  loadingArticle.value = false;
  resetCreateForm();
  await nextTick();
  await initEditor();
};

onBeforeRouteUpdate(async (to, from) => {
  if (to.name === "ArticleCreate" && from.name !== "ArticleCreate") {
    await setupCreateForm();
  } else if (to.name === "ArticleEdit" && to.params.id !== from.params.id) {
    loadingArticle.value = true;
    await loadArticle();
    await nextTick();
    if (quillEditor) {
      quillEditor.root.innerHTML = formData.value.content || "";
    }
  }
});

const onPageShow = (event) => {
  if (!isEdit.value && event.persisted) {
    setupCreateForm();
  }
};

onMounted(async () => {
  window.addEventListener("pageshow", onPageShow);
  if (isEdit.value) {
    await loadArticle();
  }
  await nextTick();
  await initEditor();
});

onBeforeUnmount(() => {
  window.removeEventListener("pageshow", onPageShow);
  destroyEditor();
});
</script>

<style scoped>
.article-form-card {
  box-shadow: 0 10px 30px rgba(21, 66, 18, 0.08);
}

.field-label {
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--color-on-surface, #1b1c1c);
  margin-bottom: 0.5rem;
}

.field-input {
  width: 100%;
  padding: 0.625rem 1rem;
  border-radius: 0.5rem;
  border: 1px solid rgba(194, 201, 187, 0.5);
  background: var(--color-surface, #fbf9f8);
  color: var(--color-on-surface, #1b1c1c);
}

.field-input:focus {
  outline: none;
  box-shadow: 0 0 0 2px var(--color-primary, #154212);
}

.dashboard-primary-btn {
  padding: 0.5rem 1.25rem;
  border-radius: 9999px;
  background-color: #154212;
  color: #a9f862;
  font-weight: 600;
  border: none;
  box-shadow: 0 4px 10px rgba(21, 66, 18, 0.25);
  transition: background-color 0.15s ease, box-shadow 0.15s ease,
    transform 0.1s ease;
}

.dashboard-primary-btn:hover:not(:disabled) {
  background-color: #2d5a27;
  box-shadow: 0 6px 16px rgba(21, 66, 18, 0.3);
  transform: translateY(-1px);
}

.dashboard-primary-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.dashboard-secondary-btn {
  padding: 0.5rem 1.1rem;
  border-radius: 9999px;
  border: 1px solid rgba(21, 66, 18, 0.2);
  background: rgba(255, 255, 255, 0.9);
  color: #154212;
  font-weight: 500;
  font-size: 0.875rem;
  transition: background-color 0.15s ease, border-color 0.15s ease,
    color 0.15s ease;
}

.dashboard-secondary-btn:hover:not(:disabled) {
  background: #f5f3f3;
  border-color: #154212;
}

.dashboard-secondary-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.content-editor-block {
  display: flex;
  flex-direction: column;
}

.wysiwyg-editor-container {
  position: relative;
  width: 100%;
  min-height: 300px;
  overflow: hidden;
}

.wysiwyg-editor-container :deep(.ql-toolbar) {
  border-color: rgba(194, 201, 187, 0.5);
  border-radius: 0.5rem 0.5rem 0 0;
  background: white;
}

.wysiwyg-editor-container :deep(.ql-container) {
  position: relative;
  font-family: inherit;
  font-size: 15px;
  min-height: 260px;
  border-color: rgba(194, 201, 187, 0.5);
  border-radius: 0 0 0.5rem 0.5rem;
}

.wysiwyg-editor-container :deep(.ql-editor) {
  position: relative;
  min-height: 260px;
  color: #1b1c1c;
}

.wysiwyg-editor-container :deep(.ql-editor.ql-blank::before) {
  content: attr(data-placeholder);
  position: absolute;
  left: 15px;
  right: 15px;
  top: 12px;
  color: #9ca3af;
  font-style: normal;
  pointer-events: none;
}

.wysiwyg-editor-container :deep(.ql-picker-options) {
  max-height: 200px;
  overflow-y: auto;
}

.wysiwyg-editor-container :deep(.ql-editor img) {
  max-width: 100%;
  height: auto;
}

.wysiwyg-editor-container.editor-image-uploading :deep(.ql-image) {
  opacity: 0.5;
  pointer-events: none;
}

@media (min-width: 1024px) {
  .wysiwyg-editor-container {
    min-height: 400px;
  }

  .wysiwyg-editor-container :deep(.ql-container),
  .wysiwyg-editor-container :deep(.ql-editor) {
    min-height: 360px;
  }
}
</style>
